<template>
  <v-container>
    <div class="elevation-2 px-5 mt-3 hide-on-print">
      <SimplePageHeader title="Time Card" class="px-4 pr-5 mb-5">
        <template v-slot:right-buttons>
          <ButtonCustomIcon
            icon="cloud_download"
            color="primary"
            text="Excel"
            class="hide-on-print"
            :block="true"
            @click="handleExportToExcel"
          />
          <ButtonPrint
            class="mt-3"
            :block="true"
          />
        </template>
      </SimplePageHeader>
      <div class="px-5 mb-sm-5">
        <v-row align="center" justify="start">
          <v-col cols="12" sm="6" md="3" lg="3" xl="2">
            <SimpleDatePicker
              label="Start Date"
              :value="startDate"
              @selected-date-change="(newValue) => startDate = newValue"
            ></SimpleDatePicker>
          </v-col>
          <v-col cols="12" sm="6" md="3" lg="3" xl="2">
            <SimpleDatePicker
              label="End Date"
              :value="endDate"
              :min-date="startDate"
              :min-date-enable="true"
              @selected-date-change="(newValue) => endDate = newValue"
            ></SimpleDatePicker>
          </v-col>
        </v-row>
      </div>
    </div>
    <div v-if="!showNoDataMessage">
      <ReportHeader
        reportTitle="Time Card Report"
        :reportCriteria="reportFilterCriteria"
      ></ReportHeader>
      <v-row class="mt-4">
        <v-col cols="12">
          <v-card>
            <v-card-text>
              <div class="py-3 px-5">
                <v-row>
                  <v-col cols="12" sm="6" md="4" align="center">
                    <h2>Consultant</h2>
                    <span class="headline">{{ timeCardReport.assignment.userFullName }}</span>
                  </v-col>
                  <v-col sm="6" md="4" align="center">
                    <h2>Client</h2>
                    <span class="headline">{{ timeCardReport.assignment.clientName }}</span>
                  </v-col>
                  <v-col sm="6" md="4" align="center">
                    <h2>Period</h2>
                    <span class="headline">{{ startDate | formatDate }} - {{ endDate | formatDate }}</span>
                  </v-col>
                  <v-col cols="12" sm="4" align="center">
                    <h2>Total Billable Hours</h2>
                    <span class="headline">{{ timeCardReport.totalBillableHours | formatDecimal }}</span>
                  </v-col>
                  <v-col cols="12" sm="4" align="center">
                    <h2>ST</h2>
                    <span class="headline">{{ timeCardReport.stHours | formatDecimal }}</span>
                  </v-col>
                  <v-col cols="12" sm="4" align="center">
                    <h2>OT</h2>
                    <span class="headline">{{ timeCardReport.otHours | formatDecimal }}</span>
                  </v-col>
                </v-row>
              </div>
            </v-card-text>
          </v-card>
        </v-col>
        <v-col cols="12">
          <v-card>
            <v-card-text>
              <div class="py-3 px-5">
                <v-row>
                  <v-col cols="12" sm="6" md="4" align="center" class="hide-on-print">
                    <h2>Bill Rate</h2>
                    <span class="headline">{{ timeCardReport.assignment.billRate | currency }} {{ timeCardReport.assignment.billRateTypeId | getBillRateTypeLabel }}</span>
                  </v-col>
                  <v-col cols="12" sm="6" md="4" align="center">
                    <h2>Invoice Frequency</h2>
                    <span class="headline">{{ timeCardReport.assignment.invoiceFrequencyId | getInvoicingFrequencyLabel }}</span>
                  </v-col>
                  <v-col v-if="timeCardReport.totalContractorCommissionWithMarkup" cols="12" sm="6" md="4" align="center" class="hide-on-print">
                    <h2>Contractor Commission w/ Markup</h2>
                    <span class="headline">{{ timeCardReport.totalContractorCommissionWithMarkup | currency }}</span>
                  </v-col>
                  <v-col sm="6" md="4" align="center" class="hide-on-print">
                    <h2>Total Bill</h2>
                    <span class="headline">{{ timeCardReport.totalBillAmount | currency }}</span>
                  </v-col>
                </v-row>
              </div>
            </v-card-text>
          </v-card>
        </v-col>
        <v-col cols="12">
          <v-card>
            <v-card-text>
              <div class="py-3 px-5">
                <v-row>
                  <v-col cols="12" sm="6" md="4" align="center" class="hide-on-print">
                    <h2>Pay Rate</h2>
                    <span class="headline">{{ timeCardReport.assignment.payRate | currency }} {{ timeCardReport.assignment.payRateTypeId | getPayRateTypeLabel }}</span>
                  </v-col>
                  <v-col cols="12" sm="6" md="4" align="center" class="hide-on-print">
                    <h2>Pay Frequency</h2>
                    <span class="headline">{{ timeCardReport.assignment.payFrequencyId | getPayFrequencyLabel }}</span>
                  </v-col>
                  <v-col v-if="timeCardReport.totalContractorCommission" cols="12" sm="6" md="4" align="center" class="hide-on-print">
                    <h2>Contractor Commission</h2>
                    <span class="headline">{{ timeCardReport.totalContractorCommission | currency }}</span>
                  </v-col>
                  <v-col cols="12" sm="6" md="4" align="center" class="hide-on-print">
                    <h2>Total Pay</h2>
                    <span class="headline">{{ timeCardReport.totalPayAmount | currency }}</span>
                  </v-col>
                </v-row>
              </div>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
      <v-container fluid class="px-0 pt-2 mt-4">
        <v-layout column>
          <v-data-table
            dense
            :headers="headers"
            :items="timeCardReport.timeCardEntries"
            class="elevation-2"
          >
            <template v-slot:item.date="{ item }">
              {{ item.date | formatDate }}
            </template>
            <template v-slot:item.day="{ item }">
              {{ item.date | dayOfWeekString }}
            </template>
            <template v-slot:item.timeSheetHours="{ item }">
              {{ item.timeSheetHours | formatDecimal }}
            </template>
          </v-data-table>
        </v-layout>
      </v-container>
      <v-container fluid class="px-0 pt-2 mt-4" v-if="typeSummaries.length > 0">
        <v-layout column>
          <h2 class="px-2">Type Summary</h2>
          <v-data-table
            dense
            :headers="typeSummaryHeaders"
            :items="typeSummaries"
            hide-default-footer
            class="elevation-2"
          >
            <template v-slot:item.hours="{ item }">
              {{ item.hours | formatDecimal }}
            </template>
            <template v-slot:item.payRate="{ item }">
              <span v-if="showsTypeSummaryValue(item, 'payRate')">{{ item.payRate | currency }}</span>
            </template>
            <template v-slot:item.payAmount="{ item }">
              <span v-if="showsTypeSummaryValue(item, 'payAmount')">{{ item.payAmount | currency }}</span>
            </template>
            <template v-slot:item.billRate="{ item }">
              <span v-if="showsTypeSummaryValue(item, 'billRate')">{{ item.billRate | currency }}</span>
            </template>
            <template v-slot:item.billAmount="{ item }">
              <span v-if="showsTypeSummaryValue(item, 'billAmount')">{{ item.billAmount | currency }}</span>
            </template>
          </v-data-table>
        </v-layout>
      </v-container>
    </div>
    <div class="mt-5" v-if="showNoDataMessage">
      <NoDataAvailableMessage></NoDataAvailableMessage>
    </div>
    <BottomSpacer></BottomSpacer>
  </v-container>
</template>

<script>
import { SimplePageHeader } from '@/components/pageComponents'
import { BottomSpacer, ButtonCustomIcon, ButtonPrint } from '@/components/buttons'
import { SimpleDatePicker } from '@/components/shared/datePickers'
import { mapFields } from '@/vueHelpers/vuex'
import NoDataAvailableMessage from '@/components/reports/NoDataAvailableMessage'
import { HOURLY as HOURLY_PAY_RATE } from '@/lib/constants/payRateTypes'
import { HOURLY as HOURLY_BILL_RATE } from '@/lib/constants/billRateTypes'
import { mapActions } from 'vuex'
import { formatDate, dayOfWeekString } from '@/lib/display'
import excel from '@/lib/export/excel'
import { STAFF, CONSULTANTS } from '@/lib/constants/reportTypes'
import ReportHeader from '@/components/reports/ReportHeader'
import { showsTypeSummaryValue, toTypeSummaryExportRow } from './timeCardTypeSummaryHelpers'

export default {
  components: {
    NoDataAvailableMessage,
    SimplePageHeader,
    SimpleDatePicker,
    BottomSpacer,
    ButtonCustomIcon,
    ButtonPrint,
    ReportHeader
  },
  data () {
    return {
      STAFF,
      CONSULTANTS,
      showNoDataMessage: true,
      isInitialized: false
    }
  },
  computed: {
    ...mapFields('timeCardReport', ['timeCardReport']),
    ...mapFields('timeCardReportFilters', [
      'reportFilterType',
      'startDate',
      'endDate',
      'assignmentId'
    ]),
    reportFilterCriteria: {
      get () {
        return [
          { 'key': 'Date Begin', 'value': formatDate(this.startDate) },
          { 'key': 'Date End', 'value': formatDate(this.endDate) }
        ]
      }
    },
    headers: {
      get () {
        return [
          { text: 'Date', value: 'date', sortable: false, align: 'center' },
          { text: 'Day', value: 'day', sortable: false, align: 'start' },
          { text: 'Type', value: 'timeTypeName', sortable: false, align: 'center' },
          { text: 'Hours', value: 'timeSheetHours', sortable: false, align: 'center' }
        ]
      }
    },
    typeSummaryHeaders: {
      get () {
        let headers = [
          { text: 'Type', value: 'timeTypeName', sortable: false, align: 'center' },
          { text: 'Hours', value: 'hours', sortable: false, align: 'center' }
        ]
        if (this.isHourlyPaid) {
          headers.push({ text: 'Pay Rate', value: 'payRate', sortable: false, align: 'center', class: 'hide-on-print', cellClass: 'hide-on-print' })
          headers.push({ text: 'Pay Amount', value: 'payAmount', sortable: false, align: 'center', class: 'hide-on-print', cellClass: 'hide-on-print' })
        }
        if (this.isHourlyBilled) {
          headers.push({ text: 'Bill Rate', value: 'billRate', sortable: false, align: 'center' })
          headers.push({ text: 'Bill Amount', value: 'billAmount', sortable: false, align: 'center' })
        }
        return headers
      }
    },
    typeSummaries () {
      return this.timeCardReport.typeSummaries || []
    },
    showStandardTime () {
      return (this.timeCardReport.timeCardEntries && this.timeCardReport.timeCardEntries.length > 0) ||
          (this.isHourlyPaid || this.isHourlyBilled)
    },
    isHourlyPaid () {
      return this.timeCardReport.assignment.payRateTypeId === HOURLY_PAY_RATE
    },
    isHourlyBilled () {
      return this.timeCardReport.assignment.billRateTypeId === HOURLY_BILL_RATE
    },
    isDisplayingConsultants () {
      return this.reportFilterType === this.CONSULTANTS
    },
    dateFilter () {
      return { startDate: this.startDate, endDate: this.endDate }
    }
  },
  watch: {
    dateFilter () {
      if (this.isInitialized && this.assignmentId && this.startDate && this.endDate) {
        this.getTimeCardReportWithFilters().then(() => {
          this.showNoDataMessage = false
        })
      }
    }
  },
  methods: {
    showsTypeSummaryValue,
    ...mapActions('timeCardReport', [
      'getTimeCardReportWithFilters'
    ]),
    ...mapActions('timeCardReportFilters', ['clearFilters']),
    handleExportToExcel () {
      if (this.timeCardReport &&
        this.timeCardReport.timeCardEntries &&
        this.timeCardReport.timeCardEntries.length > 0) {
        const dataToExport = {}

        dataToExport['timeCard'] = {
          headers: [this.headers.map(header => header.text)],
          rows: this.timeCardReport.timeCardEntries.map(entry => ({
            date: formatDate(entry.date),
            day: dayOfWeekString(entry.date),
            timeTypeName: entry.timeTypeName,
            timeSheetHours: entry.timeSheetHours
          })),
          name: 'Time Card'
        }

        if (this.typeSummaries.length > 0) {
          const typeSummaryKeys = this.typeSummaryHeaders.map(header => header.value)
          dataToExport['typeSummary'] = {
            headers: [this.typeSummaryHeaders.map(header => header.text)],
            rows: this.typeSummaries.map(summary => toTypeSummaryExportRow(summary, typeSummaryKeys)),
            name: 'Type Summary'
          }
        }

        excel.exportObject2Excel({ data: dataToExport, fileName: 'TimeCard' })
      }
    },
    searchTimeCardEntriesHandler () {
      this.showNoDataMessage = !this.timeCardReport.assignment.id
    }
  },
  mounted () {
    if (this.$route.query.startDate) {
      this.startDate = this.$route.query.startDate
    }
    if (this.$route.query.endDate) {
      this.endDate = this.$route.query.endDate
    }
    if (this.$route.query.assignmentId) {
      this.assignmentId = parseInt(this.$route.query.assignmentId)
    }

    if (
      (this.assignmentId && this.startDate && this.endDate)) {
      this.getTimeCardReportWithFilters().then(() => {
        this.showNoDataMessage = false
      })
    } else {
      this.clearFilters()
      this.showNoDataMessage = true
    }
    this.$nextTick(() => {
      this.isInitialized = true
    })
  }
}
</script>
